import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Star, HardHat, CheckCircle2, RefreshCw } from "lucide-react";

/**
 * Inline vendor auto-suggest widget for a maintenance ticket.
 * Fetches GET /api/vendors/suggest?category=X&property_id=Y and renders
 * the top-3 with a one-click "Assign" button.
 *
 * Props:
 *   ticket        — the maintenance ticket (must include id + category + property_id)
 *   onAssigned    — callback fired after successful assignment
 *   compact       — if true, renders 1 primary suggestion + expand
 */
export default function VendorSuggestBar({ ticket, onAssigned, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const [assigning, setAssigning] = useState(null);

  const load = async () => {
    if (!ticket?.category) return;
    setLoading(true);
    try {
      const params = { category: ticket.category };
      if (ticket.property_id) params.property_id = ticket.property_id;
      const { data } = await api.get("/vendors/suggest", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [ticket?.category, ticket?.property_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async (v) => {
    setAssigning(v.id);
    try {
      await api.post(`/maintenance/${ticket.id}/assign-vendor`, { vendor_id: v.id });
      toast.success(`Assigned to ${v.name}`);
      onAssigned?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setAssigning(null);
  };

  if (ticket?.assigned_vendor) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-emerald-300" data-testid={`vendor-assigned-${ticket.id}`}>
        <CheckCircle2 className="h-3.5 w-3.5" /> Assigned to <b>{ticket.assigned_vendor}</b>
      </div>
    );
  }

  if (loading && items.length === 0) {
    return <div className="mt-3 text-[11px] text-zinc-500">Finding the right vendors…</div>;
  }
  if (!loading && items.length === 0) {
    return (
      <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1" data-testid={`vendor-suggest-none-${ticket.id}`}>
        <Sparkles className="h-3 w-3" /> No matching vendors — add one under Directory → Vendors.
      </div>
    );
  }

  const visible = expanded ? items : items.slice(0, 1);

  return (
    <div className="mt-3 rounded-md border border-orange-500/20 bg-orange-500/[0.03] p-3" data-testid={`vendor-suggest-${ticket.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-orange-300 flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Suggested vendors ({items.length})
        </div>
        <button onClick={load} className="text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      <div className="space-y-1.5">
        {visible.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-2 rounded-md bg-black/20 border border-white/5 p-2"
               data-testid={`vendor-suggest-row-${v.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <HardHat className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
                <div className="font-medium text-sm truncate">{v.name}</div>
                <Badge variant="outline" className="border-white/10 text-[10px]">{v.category}</Badge>
                <div className="text-[10px] text-amber-300 flex items-center gap-0.5"><Star className="h-2.5 w-2.5" fill="currentColor" />{v.rating}</div>
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{v._match_reason}</div>
            </div>
            <Button size="sm"
                    onClick={() => assign(v)}
                    disabled={assigning === v.id}
                    className="bg-orange-500 hover:bg-orange-600 h-7 text-[11px] gap-1 flex-shrink-0"
                    data-testid={`vendor-assign-${v.id}`}>
              {assigning === v.id ? "…" : "Assign"}
            </Button>
          </div>
        ))}
      </div>
      {!expanded && items.length > 1 && (
        <button onClick={() => setExpanded(true)}
                className="mt-2 text-[10px] text-zinc-500 hover:text-orange-300"
                data-testid={`vendor-suggest-more-${ticket.id}`}>
          Show {items.length - 1} more candidate{items.length - 1 === 1 ? "" : "s"}…
        </button>
      )}
    </div>
  );
}
